import React, { useState, useCallback, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Box,
    Text,
    Badge,
    Alert,
    AlertIcon,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    useDisclosure,
    Grid,
    GridItem,
    Tag,
    TagLabel,
    Flex,
    Icon,
    Tooltip,
    useColorModeValue,
    Select,
    Textarea,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Checkbox,
    Divider
} from '@chakra-ui/react';
import {
    MdDelete,
    MdNotifications,
    MdEvent,
    MdSchedule,
    MdPeople,
    MdBusiness,
    MdEmail
} from 'react-icons/md';

import apiService from '../../services/apiService';
import EventScheduleModal from './EventScheduleModal';
import { formatDateTime, getStatusColor } from '../../utils/helpers';
import { ToastManager } from '../Common/ToastManager';

/**
 * Event Details Modal Component with Status Management
 */
function EventDetailsModal({ 
    isOpen, 
    onClose, 
    event, 
    onEventUpdated, 
    onEventDeleted,
    userProfile 
}) {
    const [loading, setLoading] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(event?.devopsStatus || 'Scheduled');
    const [statusNote, setStatusNote] = useState('');
    const [statusError, setStatusError] = useState('');
    const [showStatusForm, setShowStatusForm] = useState(false);
    
    // Completion checklist state
    const [completionChecklist, setCompletionChecklist] = useState({
        appMappingsVerified: false,
        versionChangesConfirmed: false,
        deploymentTested: false,
        documentationUpdated: false,
        stakeholdersNotified: false
    });
    const [showCompletionChecklist, setShowCompletionChecklist] = useState(false);
    
    const {
        isOpen: isDeleteAlertOpen,
        onOpen: onDeleteAlertOpen,
        onClose: onDeleteAlertClose
    } = useDisclosure();
    
    const cancelRef = React.useRef();
    
    const bgColor = useColorModeValue('white', 'gray.800');
    const cardBg = useColorModeValue('gray.50', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const messageBg = useColorModeValue('white', 'gray.600');
    const messageBorderColor = useColorModeValue('gray.200', 'gray.500');

    useEffect(() => {
        if (event?.devopsStatus) {
            setSelectedStatus(event.devopsStatus);
            setShowStatusForm(false);
            setStatusNote('');
            setStatusError('');
            
            // Show checklist if status is already Completed
            setShowCompletionChecklist(event.devopsStatus === 'Completed');
        }
    }, [event]);

    useEffect(() => {
        if (!isOpen) {
            // Reset state when modal closes
            setShowStatusForm(false);
            setStatusNote('');
            setStatusError('');
            setCompletionChecklist({
                appMappingsVerified: false,
                versionChangesConfirmed: false,
                deploymentTested: false,
                documentationUpdated: false,
                stakeholdersNotified: false
            });
            setShowCompletionChecklist(false);
        }
    }, [isOpen]);

    // Show/hide checklist based on status changes
    useEffect(() => {
        if (selectedStatus === 'Completed') {
            setShowCompletionChecklist(true);
        } else {
            setShowCompletionChecklist(false);
        }
    }, [selectedStatus]);
    
    if (!event) return null;
    
    const canChangeStatus = () => {
        return userProfile && 
               (userProfile.role === 'admin' || userProfile.role === 'devops');
    };
    
    const isEventCompleted = () => {
        return !event.isCompleted;
    }
    
    const canDelete = () => {
        return userProfile && userProfile.role === 'admin';
    };
    
    const handleStatusChange = (e) => {
        const newStatus = e.target.value;
        setSelectedStatus(newStatus);
        
        // Show form when status changes from current status
        if (newStatus !== event?.devopsStatus) {
            setShowStatusForm(true);
            setStatusError('');
        } else {
            setShowStatusForm(false);
            setStatusNote('');
            setStatusError('');
        }
    };

    // Handle checklist changes
    const handleChecklistChange = (field) => {
        setCompletionChecklist(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    // Check if all checklist items are checked
    const isChecklistComplete = () => {
        return Object.values(completionChecklist).every(checked => checked);
    };
    
    const handleStatusSubmit = async () => {
        // Validate note is provided
        if (!statusNote.trim()) {
            setStatusError('Note is required when changing status');
            return;
        }

        // Validate checklist if status is Completed
        if (selectedStatus === 'Completed' && !isChecklistComplete()) {
            setStatusError('Please complete all checklist items before marking as Completed');
            ToastManager.showError('Please complete all checklist items before marking as Completed');
            return;
        }
        
        try {
            setLoading(true);
            console.log(userProfile);
            // Call API to update status using apiService
            const response = await apiService.updateEventDevopsStatus({
                eventId: event.id,
                devopsStatus: selectedStatus,
                devopsMessage: statusNote,
                updatedBy: userProfile?.email || userProfile?.displayName || 'Unknown'
            });
            
            if (response?.success) {
                // Show success message
                ToastManager.showSuccess('Status updated successfully');
                
                // Reset form
                setShowStatusForm(false);
                setStatusNote('');
                setStatusError('');
                setCompletionChecklist({
                    appMappingsVerified: false,
                    versionChangesConfirmed: false,
                    deploymentTested: false,
                    documentationUpdated: false,
                    stakeholdersNotified: false
                });
                setShowCompletionChecklist(false);
                
                // Notify parent to refresh data
                if (onEventUpdated) {
                    onEventUpdated();
                }
                
                // Close modal
                onClose();
            } else {
                setStatusError('Failed to update Event status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            setStatusError(error?.message || 'Failed to update status');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSendNotification = async () => {
        try {
            setLoading(true);
            const response = await apiService.sendEventNotification(event.id);
            
            if (response?.success) {
                ToastManager.showSuccess('Event notification sent successfully');
            } else {
                ToastManager.showError(response?.message || 'Failed to send notification');
            }
        } catch (error) {
            console.error('Error sending notification:', error);
            ToastManager.showError('Failed to send notification');
        } finally {
            setLoading(false);
        }
    };
    
    const handleDelete = async () => {
        if (!canDelete()) {
            ToastManager.showError('You do not have permission to delete this event');
            return;
        }
        
        try {
            setLoading(true);
            const response = await apiService.deleteEventSchedule(event.id);
            
            if (response?.success) {
                onDeleteAlertClose();
                onEventDeleted();
            } else {
                ToastManager.showError(response?.message || 'Failed to delete event');
            }
        } catch (error) {
            console.error('Error deleting event:', error);
            ToastManager.showError('Failed to delete event');
        } finally {
            setLoading(false);
        }
    };
    
    const getEventTypeBadgeColor = (eventType) => {
        switch (eventType) {
            case 'Deployment':
                return 'blue';
            case 'Upgrade':
                return 'yellow';
            case 'Maintenance':
                return 'red';
            default:
                return 'gray';
        }
    };
    
    const getStatusColorLocal = (status) => {
        switch (status) {
            case 'Scheduled':
                return 'blue';
            case 'In Progress':
                return 'orange';
            case 'Completed':
                return 'green';
            case 'Pending':
                return 'red';
            case 'rollback':
                return 'purple';
            default:
                return 'gray';
        }
    };
    
    const parseEmailAddresses = (emailString) => {
        if (!emailString) return [];
        try {
            if (emailString.startsWith('[')) {
                return JSON.parse(emailString);
            } else {
                return emailString.split(/[,;]/).map(email => email.trim()).filter(email => email);
            }
        } catch {
            return emailString.split(/[,;]/).map(email => email.trim()).filter(email => email);
        }
    };
    
    return (
        <>
            <Modal 
                isOpen={isOpen} 
                onClose={onClose} 
                size="4xl" 
                scrollBehavior="inside"
                isCentered
            >
                <ModalOverlay />
                <ModalContent bg={bgColor} maxH="90vh">
                    <ModalHeader>
                        <HStack spacing={3}>
                            <Text>Event Details</Text>
                            <Badge 
                                colorScheme={getEventTypeBadgeColor(event.eventType)}
                                fontSize="sm"
                                px={2}
                                py={1}
                            >
                                {event.eventType}
                            </Badge>
                            <Badge 
                                colorScheme={getStatusColorLocal(event.devopsStatus)}
                                fontSize="sm" 
                                px={2} 
                                py={1}
                            >
                                {event?.devopsStatus || 'Scheduled'}
                            </Badge>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    
                    <ModalBody pb={6}>
                        <VStack spacing={6} align="stretch">
                            {/* Status Management Section */}
                            {canChangeStatus() && isEventCompleted() && (
                                <Box bg={cardBg} p={4} borderRadius="md" borderWidth="2px" borderColor="blue.200">
                                    <Text fontSize="lg" fontWeight="semibold" mb={3}>Update Event Status</Text>
                                    
                                    <FormControl isInvalid={!!statusError}>
                                        <FormLabel>Status</FormLabel>
                                        <Select 
                                            value={selectedStatus} 
                                            onChange={handleStatusChange}
                                            size="md"
                                            mb={3}
                                        >
                                            <option value="Scheduled">Scheduled</option>
                                            <option value="In Progress">In Progress</option>
                                            <option value="Completed">Completed</option>
                                            <option value="Pending">Pending</option>
                                            <option value="rollback">Rollback</option>
                                        </Select>
                                        
                                        {/* Completion Checklist - Show when status is Completed */}
                                        {showCompletionChecklist && (
                                            <>
                                                <Divider my={4} />
                                                
                                                <Box mb={4}>
                                                    <HStack mb={3} justifyContent="space-between">
                                                        <HStack>
                                                            <Text fontSize="md" fontWeight="semibold">
                                                                Completion Checklist
                                                            </Text>
                                                            {!isChecklistComplete() && (
                                                                <Tag colorScheme="orange" size="sm">
                                                                    Required
                                                                </Tag>
                                                            )}
                                                            {isChecklistComplete() && (
                                                                <Tag colorScheme="green" size="sm">
                                                                    Verified ✓
                                                                </Tag>
                                                            )}
                                                        </HStack>
                                                    </HStack>
                                                    
                                                    <Alert status="info" mb={3} size="sm">
                                                        <AlertIcon />
                                                        <Text fontSize="sm">
                                                            Please verify all items below before marking as Completed
                                                        </Text>
                                                    </Alert>
                                                    
                                                    <VStack spacing={2} align="stretch" pl={2}>
                                                        <Checkbox
                                                            isChecked={completionChecklist.appMappingsVerified}
                                                            onChange={() => handleChecklistChange('appMappingsVerified')}
                                                            colorScheme="blue"
                                                            size="md"
                                                        >
                                                            <Box>
                                                                <Text fontWeight="medium" fontSize="sm">
                                                                    App mappings verified and updated in Prism Monitor
                                                                </Text>
                                                                <Text fontSize="xs" color={textColor}>
                                                                    All application mappings are correct and functional
                                                                </Text>
                                                            </Box>
                                                        </Checkbox>
                                                        
                                                        <Checkbox
                                                            isChecked={completionChecklist.versionChangesConfirmed}
                                                            onChange={() => handleChecklistChange('versionChangesConfirmed')}
                                                            colorScheme="blue"
                                                            size="md"
                                                        >
                                                            <Box>
                                                                <Text fontWeight="medium" fontSize="sm">
                                                                    Version changes confirmed
                                                                </Text>
                                                                <Text fontSize="xs" color={textColor}>
                                                                    All version updates documented and validated
                                                                </Text>
                                                            </Box>
                                                        </Checkbox>
                                                        
                                                        <Checkbox
                                                            isChecked={completionChecklist.deploymentTested}
                                                            onChange={() => handleChecklistChange('deploymentTested')}
                                                            colorScheme="blue"
                                                            size="md"
                                                        >
                                                            <Box>
                                                                <Text fontWeight="medium" fontSize="sm">
                                                                    Deployment tested
                                                                </Text>
                                                                <Text fontSize="xs" color={textColor}>
                                                                    Deployment working as expected in target environment
                                                                </Text>
                                                            </Box>
                                                        </Checkbox>
                                                        
                                                        <Checkbox
                                                            isChecked={completionChecklist.documentationUpdated}
                                                            onChange={() => handleChecklistChange('documentationUpdated')}
                                                            colorScheme="blue"
                                                            size="md"
                                                        >
                                                            <Box>
                                                                <Text fontWeight="medium" fontSize="sm">
                                                                    Documentation updated
                                                                </Text>
                                                                <Text fontSize="xs" color={textColor}>
                                                                    All relevant documentation reflects changes
                                                                </Text>
                                                            </Box>
                                                        </Checkbox>
                                                        
                                                        <Checkbox
                                                            isChecked={completionChecklist.stakeholdersNotified}
                                                            onChange={() => handleChecklistChange('stakeholdersNotified')}
                                                            colorScheme="blue"
                                                            size="md"
                                                        >
                                                            <Box>
                                                                <Text fontWeight="medium" fontSize="sm">
                                                                    Stakeholders notified
                                                                </Text>
                                                                <Text fontSize="xs" color={textColor}>
                                                                    All affected parties informed of completion
                                                                </Text>
                                                            </Box>
                                                        </Checkbox>
                                                    </VStack>
                                                    
                                                    {!isChecklistComplete() && (
                                                        <Alert status="warning" mt={3} size="sm">
                                                            <AlertIcon />
                                                            <Text fontSize="sm">
                                                                {Object.values(completionChecklist).filter(v => !v).length} item(s) remaining
                                                            </Text>
                                                        </Alert>
                                                    )}
                                                </Box>
                                                
                                                <Divider my={4} />
                                            </>
                                        )}
                                        
                                        {showStatusForm && (
                                            <VStack spacing={3} align="stretch">
                                                <FormControl isInvalid={!!statusError} isRequired>
                                                    <FormLabel>Note</FormLabel>
                                                    <Textarea
                                                        value={statusNote}
                                                        onChange={(e) => {
                                                            setStatusNote(e.target.value);
                                                            setStatusError('');
                                                        }}
                                                        placeholder="Enter a note about this status change (required)"
                                                        rows={4}
                                                    />
                                                    <FormErrorMessage>{statusError}</FormErrorMessage>
                                                </FormControl>
                                                
                                                <HStack>
                                                    <Button
                                                        colorScheme="blue"
                                                        onClick={handleStatusSubmit}
                                                        isLoading={loading}
                                                        loadingText="Updating..."
                                                        isDisabled={selectedStatus === 'Completed' && !isChecklistComplete()}
                                                    >
                                                        Submit
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        onClick={() => {
                                                            setSelectedStatus(event.devopsStatus || 'Scheduled');
                                                            setShowStatusForm(false);
                                                            setStatusNote('');
                                                            setStatusError('');
                                                            setCompletionChecklist({
                                                                appMappingsVerified: false,
                                                                versionChangesConfirmed: false,
                                                                deploymentTested: false,
                                                                documentationUpdated: false,
                                                                stakeholdersNotified: false
                                                            });
                                                            setShowCompletionChecklist(false);
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </HStack>
                                            </VStack>
                                        )}
                                    </FormControl>
                                </Box>
                            )}
                            
                            {/* Event Status Alert */}
                            {event.devopsStatus === 'Completed' && (
                                <Alert status="success">
                                    <AlertIcon />
                                    <Box>
                                        <Text fontWeight="bold">Event Completed</Text>
                                        {event.completedBy && (
                                            <Text fontSize="sm">
                                                Completed by {event.completedBy} on{' '}
                                                {formatDateTime(event.completedAtUtc)}
                                            </Text>
                                        )}
                                    </Box>
                                </Alert>
                            )}
                            
                            {/* Basic Information */}
                            <Box bg={cardBg} p={4} borderRadius="md">
                                <HStack spacing={2} mb={3}>
                                    <Icon as={MdEvent} color="blue.500" />
                                    <Text fontSize="lg" fontWeight="semibold">Event Information</Text>
                                </HStack>
                                
                                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                    <GridItem>
                                        <Text fontWeight="medium" color={textColor}>Title</Text>
                                        <Text fontSize="lg">{event.eventTitle}</Text>
                                    </GridItem>
                                    
                                    <GridItem>
                                        <Text fontWeight="medium" color={textColor}>Application</Text>
                                        <Text>{event.applicationName || 'N/A'}</Text>
                                    </GridItem>
                                    
                                    {event.eventDescription && (
                                        <GridItem colSpan={2}>
                                            <Text fontWeight="medium" color={textColor}>Description</Text>
                                            <Text>{event.eventDescription}</Text>
                                        </GridItem>
                                    )}
                                </Grid>
                            </Box>
                            
                            {/* Schedule Information */}
                            <Box bg={cardBg} p={4} borderRadius="md">
                                <HStack spacing={2} mb={3}>
                                    <Icon as={MdSchedule} color="purple.500" />
                                    <Text fontSize="lg" fontWeight="semibold">Schedule</Text>
                                </HStack>
                                
                                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                    <GridItem>
                                        <Text fontWeight="medium" color={textColor}>Start Time</Text>
                                        <Text>{formatDateTime(event.startDatetimeUtc)}</Text>
                                    </GridItem>
                                    
                                    <GridItem>
                                        <Text fontWeight="medium" color={textColor}>End Time</Text>
                                        <Text>{formatDateTime(event.endDatetimeUtc)}</Text>
                                    </GridItem>
                                    
                                    <GridItem>
                                        <Text fontWeight="medium" color={textColor}>Duration</Text>
                                        <Text>
                                            {Math.round(
                                                (new Date(event.endDatetimeUtc) - new Date(event.startDatetimeUtc)) / 
                                                (1000 * 60 * 60)
                                            )} hours
                                        </Text>
                                    </GridItem>
                                    
                                    <GridItem>
                                        <Text fontWeight="medium" color={textColor}>Created By</Text>
                                        <Text>{event.createdBy}</Text>
                                    </GridItem>
                                </Grid>
                            </Box>
                            
                            {/* Affected Clients */}
                            {event.clientDetails && event.clientDetails.length > 0 && (
                                <Box bg={cardBg} p={4} borderRadius="md">
                                    <HStack spacing={2} mb={3}>
                                        <Icon as={MdBusiness} color="orange.500" />
                                        <Text fontSize="lg" fontWeight="semibold">Affected Clients</Text>
                                    </HStack>
                                    
                                    <Flex wrap="wrap" gap={2}>
                                        {event.clientDetails.map((client, index) => (
                                            <Tag key={index} size="md" colorScheme="blue">
                                                <TagLabel>
                                                    {client.clientName}
                                                    {client.version && ` (${client.version})`}
                                                </TagLabel>
                                            </Tag>
                                        ))}
                                    </Flex>
                                </Box>
                            )}
                            
                            {/* Assigned Team */}
                            {event.userDetails && event.userDetails.length > 0 && (
                                <Box bg={cardBg} p={4} borderRadius="md">
                                    <HStack spacing={2} mb={3}>
                                        <Icon as={MdPeople} color="green.500" />
                                        <Text fontSize="lg" fontWeight="semibold">Assigned Team</Text>
                                    </HStack>
                                    
                                    <Flex wrap="wrap" gap={2}>
                                        {event.userDetails.map((user, index) => (
                                            <Tag key={index} size="md" colorScheme="green">
                                                <TagLabel>
                                                    {user.displayName || user.email}
                                                    {user.role && ` (${user.role})`}
                                                </TagLabel>
                                            </Tag>
                                        ))}
                                    </Flex>
                                </Box>
                            )}
                            
                            {/* Email Configuration */}
                            <Box bg={cardBg} p={4} borderRadius="md">
                                <HStack spacing={2} mb={3}>
                                    <Icon as={MdEmail} color="teal.500" />
                                    <Text fontSize="lg" fontWeight="semibold">Email Notification</Text>
                                </HStack>
                                
                                <VStack align="stretch" spacing={3}>
                                    <Box>
                                        <Text fontWeight="medium" color={textColor}>Subject</Text>
                                        <Text>{event.emailSubject}</Text>
                                    </Box>
                                    
                                    <Box>
                                        <Text fontWeight="medium" color={textColor}>Recipients (TO)</Text>
                                        <Flex wrap="wrap" gap={1} mt={1}>
                                            {parseEmailAddresses(event.toAddresses).map((email, index) => (
                                                <Tag key={index} size="sm" colorScheme="blue">
                                                    <TagLabel>{email}</TagLabel>
                                                </Tag>
                                            ))}
                                        </Flex>
                                    </Box>
                                    
                                    {event.ccAddresses && parseEmailAddresses(event.ccAddresses).length > 0 && (
                                        <Box>
                                            <Text fontWeight="medium" color={textColor}>CC Recipients</Text>
                                            <Flex wrap="wrap" gap={1} mt={1}>
                                                {parseEmailAddresses(event.ccAddresses).map((email, index) => (
                                                    <Tag key={index} size="sm" colorScheme="green">
                                                        <TagLabel>{email}</TagLabel>
                                                    </Tag>
                                                ))}
                                            </Flex>
                                        </Box>
                                    )}
                                    
                                    {event.bccAddresses && parseEmailAddresses(event.bccAddresses).length > 0 && (
                                        <Box>
                                            <Text fontWeight="medium" color={textColor}>BCC Recipients</Text>
                                            <Flex wrap="wrap" gap={1} mt={1}>
                                                {parseEmailAddresses(event.bccAddresses).map((email, index) => (
                                                    <Tag key={index} size="sm" colorScheme="purple">
                                                        <TagLabel>{email}</TagLabel>
                                                    </Tag>
                                                ))}
                                            </Flex>
                                        </Box>
                                    )}
                                    
                                    <Box 
                                        p={3} 
                                        bg={messageBg} 
                                        borderRadius="md" 
                                        border="1px solid" 
                                        borderColor={messageBorderColor}
                                        mt={1}
                                    >                                
                                        <Text whiteSpace="pre-wrap">{event.emailBody}</Text>
                                    </Box>
                                    
                                    {event.emailSent && (
                                        <Alert status="success" size="sm">
                                            <AlertIcon />
                                            <Text fontSize="sm">
                                                Email sent on {formatDateTime(event.emailSentAtUtc)}
                                            </Text>
                                        </Alert>
                                    )}
                                </VStack>
                            </Box>
                        </VStack>
                    </ModalBody>
                    
                    <ModalFooter>
                        <HStack spacing={3}>
                            {/* <Tooltip label="Send email notification">
                                <Button
                                    leftIcon={<MdNotifications />}
                                    colorScheme="blue"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleSendNotification}
                                    isLoading={loading}
                                >
                                    Send Notification
                                </Button>
                            </Tooltip> */}
                            
                            {canDelete() && (
                                <Tooltip label="Delete event (Admin only)">
                                    <Button
                                        leftIcon={<MdDelete />}
                                        colorScheme="red"
                                        variant="outline"
                                        size="sm"
                                        onClick={onDeleteAlertOpen}
                                    >
                                        Delete
                                    </Button>
                                </Tooltip>
                            )}
                            
                            <Button variant="ghost" onClick={onClose}>
                                Close
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            
            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={onDeleteAlertClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Event Schedule
                        </AlertDialogHeader>
                        
                        <AlertDialogBody>
                            Are you sure you want to delete this event schedule? 
                            This action cannot be undone.
                            <Box mt={3} p={3} bg={cardBg} borderRadius="md">
                                <Text fontWeight="medium">"{event.eventTitle}"</Text>
                                <Text fontSize="sm" color={textColor}>
                                    {formatDateTime(event.startDatetimeUtc)} - {formatDateTime(event.endDatetimeUtc)}
                                </Text>
                            </Box>
                        </AlertDialogBody>
                        
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                                Cancel
                            </Button>
                            <Button 
                                colorScheme="red" 
                                onClick={handleDelete} 
                                ml={3}
                                isLoading={loading}
                                loadingText="Deleting..."
                            >
                                Delete Event
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
}

export default EventDetailsModal;